import React, { useState, useEffect, useCallback } from 'react';
import client from '../../api/client';
import {
  Package, Clock, Truck, CheckCircle2, ChevronRight,
  Filter, Calendar, ShoppingBag, X
} from 'lucide-react';
import { formatDate, formatDateTime, parseUTC } from '../../utils/date';
import DatePicker from '../../components/DatePicker';
import { ORDER_STATUS_LABELS, ORDER_STATUS_FLOW, ACTIVE_STATUSES } from '../../constants/statuses';
import { toast, confirmDialog } from '../../components/Toast/toast';
import { formatPrice } from '../../utils/price';

const STATUS_META = {
  pending:   { label: ORDER_STATUS_LABELS.pending,   color: 'bg-[#E8A838]/15 text-[#E8A838]', Icon: Clock },
  reserved:  { label: ORDER_STATUS_LABELS.reserved,  color: 'bg-[#E8A838]/15 text-[#E8A838]', Icon: Clock },
  paid:      { label: ORDER_STATUS_LABELS.paid,      color: 'bg-[#4A6FA5]/15 text-[#4A6FA5]', Icon: Package },
  preparing: { label: ORDER_STATUS_LABELS.preparing, color: 'bg-[#4A6FA5]/15 text-[#4A6FA5]', Icon: Truck },
  ready:     { label: ORDER_STATUS_LABELS.ready,     color: 'bg-[#6B9B74]/15 text-[#6B9B74]', Icon: CheckCircle2 },
  delivered: { label: ORDER_STATUS_LABELS.delivered, color: 'bg-[#6B9B74]/15 text-[#6B9B74]', Icon: CheckCircle2 },
  cancelled: { label: ORDER_STATUS_LABELS.cancelled, color: 'bg-[#D64545]/15 text-[#D64545]', Icon: X },
};

export default function OrdersAdmin() {
  const [tab, setTab] = useState('active');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);

  // filters for history tab
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activePicker, setActivePicker] = useState(null); // 'from' | 'to' | null

  // сортировка десктоп-таблицы (на мобиле не применяется)
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const params = {};
      const res = await client.get('/admin/orders', { params });
      setOrders(res.data || []);
    } catch {
      setOrders([]);
      setLoadError('Не удалось загрузить заказы');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const activeOrders = orders.filter(o => ACTIVE_STATUSES.includes(o.status));

  const filteredHistory = orders.filter(o => {
    // Активные заказы живут во вкладке «Активные» — в историю не попадают
    if (ACTIVE_STATUSES.includes(o.status)) return false;
    if (statusFilter && o.status !== statusFilter) return false;
    if (dateFrom) {
      const from = new Date(dateFrom); // локальная полночь — дата выбрана в календаре пользователя
      if (parseUTC(o.created_at) < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (parseUTC(o.created_at) > to) return false;
    }
    return true;
  });

  const changeStatus = async (order, newStatus) => {
    setUpdatingId(order.id);
    try {
      await client.put(`/admin/orders/${order.id}/status?status=${newStatus}`);
      await loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось изменить статус заказа');
    } finally {
      setUpdatingId(null);
    }
  };

  const cancelOrder = async (order) => {
    setUpdatingId(order.id);
    try {
      await client.post(`/admin/orders/${order.id}/cancel`);
      await loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось отменить заказ');
      throw err;
    } finally {
      setUpdatingId(null);
    }
  };

  const confirmCancelOrder = (order) => confirmDialog(
    `Отменить заказ #${String(order.id).slice(-5)}?`,
    {
      danger: true,
      okLabel: 'Отменить',
      cancelLabel: 'Отмена',
      detail: 'Товары вернутся на склад, клиент получит уведомление.',
      onConfirm: () => cancelOrder(order),
    },
  );

  const extendReserve = async (order) => {
    setUpdatingId(order.id);
    try {
      await client.post(`/admin/orders/${order.id}/extend-reserve`);
      await loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось продлить резерв');
    } finally {
      setUpdatingId(null);
    }
  };

  const getNextStatus = (current) => {
    // Для резервов — сразу «Выдан»
    if (current === 'reserved') return 'delivered';
    const idx = ORDER_STATUS_FLOW.indexOf(current);
    if (idx >= 0 && idx < ORDER_STATUS_FLOW.length - 1) return ORDER_STATUS_FLOW[idx + 1];
    return null;
  };

  const renderStatusBadge = (status) => {
    const meta = STATUS_META[status] || { label: status, color: 'bg-[#f1f5f9] text-[#0f172a]', Icon: Package };
    const { Icon } = meta;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${meta.color}`}>
        <Icon className="h-3 w-3" />
        {meta.label}
      </span>
    );
  };

  /* ── Десктоп: таблица с сортировкой (на телефоне остаются карточки) ── */

  const sortByHeader = (key) => {
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const sortOrders = (list) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sort.key === 'sum') return (Number(a.total_amount) - Number(b.total_amount)) * dir;
      if (sort.key === 'client') {
        const an = (a.first_name || a.username || '').toLowerCase();
        const bn = (b.first_name || b.username || '').toLowerCase();
        return an.localeCompare(bn) * dir;
      }
      if (sort.key === 'num') {
        // числовая строка: '10' > '9' — сначала длина, потом лексикографически
        const an = String(a.id);
        const bn = String(b.id);
        return (an.length - bn.length || an.localeCompare(bn)) * dir;
      }
      return (parseUTC(a.created_at) - parseUTC(b.created_at)) * dir;
    });
  };

  const th = (key, label, align = 'text-left') => (
    <th className={`px-4 py-3 ${align}`}>
      <button
        type="button"
        onClick={() => sortByHeader(key)}
        className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide transition hover:opacity-70 ${
          sort.key === key ? 'text-[#0f172a]' : 'text-[#64748b]'
        }`}
      >
        {label}
        <span className={sort.key === key ? 'text-[#374151]' : 'text-[#cbd5e1]'}>
          {sort.key === key && sort.dir === 'asc' ? '↑' : '↓'}
        </span>
      </button>
    </th>
  );

  const itemsSummary = (order) => {
    const items = order.items || [];
    if (!items.length) return '—';
    const names = items.slice(0, 2).map(i => i.name || i.product_name || 'Товар').join(', ');
    return items.length > 2 ? `${names} +${items.length - 2}` : names;
  };

  const renderOrdersTable = (list) => (
    <div className="hidden overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white shadow-sm md:block">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
            {th('num', '№')}
            {th('client', 'Клиент')}
            {th('date', 'Дата')}
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#64748b]">Позиции</th>
            {th('sum', 'Сумма', 'text-right')}
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#64748b]">Статус</th>
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-[#64748b]">Действия</th>
          </tr>
        </thead>
        <tbody>
          {sortOrders(list).map((order) => {
            const nextStatus = getNextStatus(order.status);
            const nextMeta = nextStatus ? STATUS_META[nextStatus] : null;
            return (
              <tr key={order.id} className="border-b border-[#e2e8f0] transition last:border-0 hover:bg-[#f8fafc]">
                <td className="px-4 py-3 font-bold text-[#0f172a]">#{String(order.id).slice(-5)}</td>
                <td className="px-4 py-3 text-[#0f172a]">{order.first_name || order.username || 'Клиент'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-[#64748b]">{formatDateTime(order.created_at)}</td>
                <td className="max-w-[20rem] truncate px-4 py-3 text-[#64748b]">{itemsSummary(order)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right font-bold text-[#0f172a]">
                  {formatPrice(order.total_amount)} ₽
                </td>
                <td className="px-4 py-3">{renderStatusBadge(order.status)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {nextStatus && (
                      <button
                        type="button"
                        onClick={() => changeStatus(order, nextStatus)}
                        disabled={updatingId === order.id}
                        className="h-8 whitespace-nowrap rounded-lg bg-[#4A6FA5] px-3 text-xs font-semibold text-white transition hover:bg-[#3D5E8E] active:scale-95 disabled:opacity-50"
                      >
                        {updatingId === order.id ? '...' : `→ ${nextMeta?.label}`}
                      </button>
                    )}
                    {(order.status === 'pending' || order.status === 'reserved') && (
                      <button
                        type="button"
                        onClick={() => confirmCancelOrder(order)}
                        disabled={updatingId === order.id}
                        className="h-8 whitespace-nowrap rounded-lg border border-[#D64545]/40 px-3 text-xs font-semibold text-[#D64545] transition hover:bg-[#D64545]/5 active:scale-95 disabled:opacity-50"
                      >
                        Отменить
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDetailOrder(order)}
                      className="h-8 whitespace-nowrap rounded-lg border border-[#e2e8f0] px-3 text-xs font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-95"
                    >
                      Детали
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderOrderCard = (order) => {
    const nextStatus = getNextStatus(order.status);
    const nextMeta = nextStatus ? STATUS_META[nextStatus] : null;

    return (
      <div key={order.id} className="rounded-xl border border-[#e2e8f0] bg-white p-3 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-[#0f172a]">#{String(order.id).slice(-5)}</span>
              {renderStatusBadge(order.status)}
            </div>
            <div className="mt-1 flex items-center gap-1 text-xs text-[#64748b]">
              <span className="truncate">{order.first_name || order.username || 'Клиент'}</span>
              <span>&middot;</span>
              <span className="shrink-0">{formatDate(order.created_at)}</span>
            </div>
          </div>
          <span className="text-sm font-bold text-[#0f172a]">{formatPrice(order.total_amount)} ₽</span>
        </div>

        {order.items && order.items.length > 0 && (
          <div className="mt-2 space-y-1">
            {order.items.slice(0, 3).map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-[#64748b]">
                <span className="truncate">{item.name || item.product_name || `Товар #${item.product_id}`}</span>
                {item.quantity && <span className="shrink-0 text-[#64748b]">×{item.quantity}</span>}
              </div>
            ))}
            {order.items.length > 3 && (
              <span className="text-xs text-[#64748b]">+{order.items.length - 3} ещё</span>
            )}
          </div>
        )}

        {/* Строка 1: только кнопка следующего статуса */}
        {nextStatus && (
          <div className="mt-2.5 flex items-center gap-2">
            <button
              onClick={() => changeStatus(order, nextStatus)}
              disabled={updatingId === order.id}
              className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-xs font-semibold text-white transition active:scale-[0.97] disabled:opacity-50 ${
                nextStatus === 'delivered' ? 'bg-[#4A6FA5] hover:bg-[#3D5E8E]' : nextStatus === 'ready' ? 'bg-[#374151] hover:bg-[#1f2937]' : 'bg-[#4A6FA5] hover:bg-[#3D5E8E]'
              }`}
            >
              {nextMeta && <nextMeta.Icon className="h-3.5 w-3.5" />}
              {updatingId === order.id ? '...' : `→ ${nextMeta?.label}`}
            </button>
          </div>
        )}

        {/* Строка 2: Отменить / +4 часа / Детали — равные, во всю ширину, единая высота h-8 */}
        <div className="mt-2 flex items-center gap-2">
          {(order.status === 'pending' || order.status === 'reserved') && (
            <button
              onClick={() => confirmCancelOrder(order)}
              disabled={updatingId === order.id}
              className="flex h-8 flex-1 items-center justify-center gap-1 rounded-lg border border-[#D64545]/40 px-3 text-xs font-semibold text-[#D64545] transition hover:bg-[#D64545]/5 active:scale-95 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              Отменить
            </button>
          )}
          {order.status === 'reserved' && (
            <button
              onClick={() => extendReserve(order)}
              disabled={updatingId === order.id}
              className="flex h-8 flex-1 items-center justify-center gap-1 rounded-lg border border-[#E8A838]/40 px-3 text-xs font-semibold text-[#E8A838] transition hover:bg-[#E8A838]/5 active:scale-95 disabled:opacity-50"
            >
              <Clock className="h-3.5 w-3.5" />
              +4 часа
            </button>
          )}
          <button
            onClick={() => setDetailOrder(order)}
            className="flex h-8 flex-1 items-center justify-center gap-1 rounded-lg border border-[#e2e8f0] px-3 text-xs font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-95"
          >
            Детали
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { key: 'active', label: 'Активные', count: activeOrders.length },
          { key: 'history', label: 'История' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`relative flex items-center justify-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium transition active:scale-[0.98] ${
              tab === t.key
                ? 'border-[#374151] bg-[#374151] text-white shadow-sm'
                : 'border-[#e2e8f0] bg-white text-[#0f172a] hover:bg-[#f8fafc]'
            }`}
          >
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className={`ml-auto grid h-5 w-5 place-items-center rounded-md text-[10px] font-bold ${
                tab === t.key ? 'bg-white/25 text-white' : 'bg-[#E8A838]/20 text-[#E8A838]'
              }`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* History filters */}
      {tab === 'history' && (
        <div className="rounded-xl border border-[#e2e8f0] bg-white p-3 space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-[#64748b]">
            <Filter className="h-3.5 w-3.5" />
            Фильтр:
          </div>
          {/* Даты */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-0.5 block text-[10px] text-[#64748b]">С</label>
              <button
                onClick={() => setActivePicker('from')}
                className="flex w-full items-center gap-2 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-2 py-1.5 text-xs text-left"
              >
                <Calendar className="h-3.5 w-3.5 text-[#64748b]" />
                <span className={dateFrom ? 'text-[#0f172a]' : 'text-[#64748b]'}>
                  {dateFrom ? dateFrom.split('-').reverse().join('.') : 'Выберите дату'}
                </span>
              </button>
            </div>
            <div>
              <label className="mb-0.5 block text-[10px] text-[#64748b]">По</label>
              <button
                onClick={() => setActivePicker('to')}
                className="flex w-full items-center gap-2 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-2 py-1.5 text-xs text-left"
              >
                <Calendar className="h-3.5 w-3.5 text-[#64748b]" />
                <span className={dateTo ? 'text-[#0f172a]' : 'text-[#64748b]'}>
                  {dateTo ? dateTo.split('-').reverse().join('.') : 'Выберите дату'}
                </span>
              </button>
            </div>
          </div>
          {/* Статус и сброс */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="mb-0.5 block text-[10px] text-[#64748b]">Статус</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] py-1.5 pr-6 pl-2 text-xs outline-none focus:border-[#374151]"
              >
                <option value="">Все</option>
                {Object.entries(STATUS_META).map(([key, meta]) => (
                  <option key={key} value={key}>{meta.label}</option>
                ))}
              </select>
            </div>
            {(dateFrom || dateTo || statusFilter) && (
              <div className="flex items-end">
                <button
                  onClick={() => { setDateFrom(''); setDateTo(''); setStatusFilter(''); }}
                  className="rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-xs text-[#64748b] hover:bg-[#f8fafc] active:scale-95"
                >
                  Сбросить
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Orders list */}
      {loadError && (
        <div className="mb-3 rounded-xl border border-[#D64545]/30 bg-[#D64545]/10 p-4 text-sm text-[#D64545]">
          {loadError}
        </div>
      )}
      {loading ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          Загрузка...
        </div>
      ) : (tab === 'active' ? activeOrders : filteredHistory).length === 0 ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          <ShoppingBag className="mx-auto mb-2 h-10 w-10 text-[#e2e8f0]" />
          <p>{tab === 'active' ? 'Нет активных заказов' : 'Заказы не найдены'}</p>
        </div>
      ) : (
        <>
          {renderOrdersTable(tab === 'active' ? activeOrders : filteredHistory)}
          <div className="space-y-2 md:hidden">
            {(tab === 'active' ? activeOrders : filteredHistory).map(renderOrderCard)}
          </div>
        </>
      )}

      {/* Detail modal */}
      {detailOrder && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#0f172a]">Заказ #{String(detailOrder.id).slice(-5)}</h2>
              <button onClick={() => setDetailOrder(null)} className="grid h-8 w-8 place-items-center rounded-full text-[#64748b] hover:bg-[#f1f5f9]">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#64748b]">Статус</span>
                {renderStatusBadge(detailOrder.status)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#64748b]">Клиент</span>
                <span className="text-sm font-medium text-[#0f172a]">{detailOrder.first_name || detailOrder.username || '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#64748b]">Дата</span>
                <span className="text-sm text-[#0f172a]">{formatDateTime(detailOrder.created_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#64748b]">Сумма</span>
                <span className="text-sm font-bold text-[#0f172a]">{formatPrice(detailOrder.total_amount)} ₽</span>
              </div>

              {detailOrder.items && detailOrder.items.length > 0 && (
                <div>
                  <span className="text-xs text-[#64748b]">Товары</span>
                  <div className="mt-1 space-y-1.5 rounded-lg bg-[#f8fafc] p-2.5">
                    {detailOrder.items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="truncate text-[#0f172a]">{item.name || item.product_name || `Товар #${item.product_id}`}</span>
                        <span className="shrink-0 text-[#64748b]">×{item.quantity || 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailOrder.address && (
                <div className="flex items-start justify-between">
                  <span className="text-xs text-[#64748b]">Адрес</span>
                  <span className="max-w-[60%] text-right text-sm text-[#0f172a]">{detailOrder.address}</span>
                </div>
              )}

              {detailOrder.comment && (
                <div className="flex items-start justify-between">
                  <span className="text-xs text-[#64748b]">Комментарий</span>
                  <span className="max-w-[60%] text-right text-sm text-[#0f172a]">{detailOrder.comment}</span>
                </div>
              )}
            </div>

            <button
              onClick={() => setDetailOrder(null)}
              className="mt-5 w-full rounded-xl border border-[#e2e8f0] py-2.5 text-sm font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-[0.98]"
            >
              Закрыть
            </button>
          </div>
        </div>
      )}

      {/* Date Picker Modal */}
      {activePicker && (
        <DatePicker
          value={activePicker === 'from' ? dateFrom : dateTo}
          onChange={val => activePicker === 'from' ? setDateFrom(val) : setDateTo(val)}
          onClose={() => setActivePicker(null)}
        />
      )}
    </div>
  );
}
